import React from 'react'

import { Button, Toolbar, Typography } from '@material-ui/core'
import { ArrowDropDown, ArrowDropDownCircle } from '@material-ui/icons'

import { DatePicker, DatePickerProps, TimePicker, TimePickerProps } from '@material-ui/pickers'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'

import { makeAppStyles } from '../../themes'

import { formatLocal } from '@percept/utils'


type ViewType = 'date' | 'year' | 'month' | 'hours' | 'minutes' | 'seconds'

type DatePickerToolbarProps = {
  date: MaterialUiPickersDate
  openView: ViewType
  setOpenView: (view: ViewType) => void
}


const useToolbarStyles = makeAppStyles( theme => ({
  root: ({ color }: { color: 'primary' | 'secondary' }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    height: '100px',
    justifyContent: 'center',
    backgroundColor: theme.palette[color].main,
    color: theme.palette[color].contrastText,
  }),
  button: {
    padding: 0,
    minWidth: 16,
    textTransform: 'none',
  },
  icon: {
    color: 'inherit',
    opacity: 0.70,
    fontSize: '0.9rem',
    marginLeft: theme.spacing(1),
  },
  selectedIcon: {
    color: 'inherit',
    opacity: 0.85,
    fontSize: '0.9rem',
    marginLeft: theme.spacing(1),
  },
  text: ({ color }: { color: 'primary' | 'secondary' }) => ({
    color: theme.palette[color].contrastText,
    opacity: 0.75,
  }),
  selectedText: ({ color }: { color: 'primary' | 'secondary' }) => ({
    color: theme.palette[color].contrastText,
  }),
}))

/* eslint-disable react/prop-types */
const ThemedToolbarComponent = ({
  classes,
  date,
  openView,
  setOpenView,
}: (
  DatePickerToolbarProps & {
    classes: Record<'root' | 'button' | 'icon' | 'selectedIcon' | 'selectedText' | 'text', string>
  }
)): JSX.Element => {
  const dateObject = new Date(date && date.toISOString() || new Date())
  const yearTypographyClassName = openView === 'year' ? classes.selectedText : classes.text
  const dateTypographyClassName = openView === 'date' ? classes.selectedText : classes.text
  const selectedIcon = <ArrowDropDownCircle className={classes.selectedIcon} />
  const unselectedIcon =  <ArrowDropDown className={classes.icon} />
  return (
    <Toolbar
      classes={{
        root: classes.root,
      }}>
      <Button className={classes.button} onClick={(): void => setOpenView('year')}>
        <Typography className={yearTypographyClassName} variant='h6'>
          { formatLocal(dateObject, 'yyyy') }
        </Typography>
        {
          openView === 'year' ? 
            selectedIcon : unselectedIcon
        }
      </Button>
      <Button className={classes.button} onClick={(): void => setOpenView('date')}>
        <Typography className={dateTypographyClassName} variant='h4'>
          { formatLocal(dateObject, 'EEE, MMM dd')}
        </Typography>
        {
          openView === 'date' ? 
            selectedIcon : unselectedIcon
        }
      </Button>
    </Toolbar>
  )
}


export type DateInputProps = {
  color?: 'primary' | 'secondary'
} & DatePickerProps

export const DateInput = ({
  color = 'primary',
  ...props
}: DateInputProps): JSX.Element => {
  const toolbarClasses = useToolbarStyles({ color })

  const ToolbarComponent = (props: DatePickerToolbarProps): JSX.Element => (
    <ThemedToolbarComponent
      classes={toolbarClasses}
      date={props.date}
      openView={props.openView}
      setOpenView={props.setOpenView} />
  )
  return (
    <DatePicker
      ToolbarComponent={ToolbarComponent}
      {...props} />
  )
}


const useTimeToolbarStyles = makeAppStyles( theme => ({
  root: ({ color }: { color: 'primary' | 'secondary' }) => ({
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    height: '100px',
    justifyContent: 'center',
    backgroundColor: theme.palette[color].main,
    color: theme.palette[color].contrastText,
  }),
  timeContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
  },
  button: {
    padding: 0,
    minWidth: 16,
    textTransform: 'none',
  },
  text: ({ color }: { color: 'primary' | 'secondary' }) => ({
    color: theme.palette[color].contrastText,
    opacity: 0.75,
  }),
  selectedText: ({ color }: { color: 'primary' | 'secondary' }) => ({
    color: theme.palette[color].contrastText,
  }),
  separator: {
    margin: theme.spacing(0, 0.5),
    opacity: 0.75,
  },
}))


/* eslint-disable react/prop-types */
const ThemedTimeToolbarComponent = ({
  color,
  date,
  openView,
  setOpenView,
}: (
  DatePickerToolbarProps & {
    color: 'primary' | 'secondary'
  }
)): JSX.Element => {
  const dateObject = new Date(date && date.toISOString() || new Date())
  const classes = useTimeToolbarStyles({ color })
  const hoursTypographyClassName = openView === 'hours' ? classes.selectedText : classes.text
  const minutesTypographyClassName = openView === 'minutes' ? classes.selectedText : classes.text
  return (
    <Toolbar
      classes={{
        root: classes.root,
      }}>
      <div className={classes.timeContainer}>
        <Button className={classes.button} onClick={(): void => setOpenView('hours')}>
          <Typography className={hoursTypographyClassName} variant='h2'>
            { formatLocal(dateObject, 'HH') }
          </Typography>
        </Button>
        <Typography className={classes.separator} variant='h2'>:</Typography>
        <Button className={classes.button} onClick={(): void => setOpenView('minutes')}>
          <Typography className={minutesTypographyClassName} variant='h2'>
            { formatLocal(dateObject, 'mm')}
          </Typography>
        </Button>
      </div>
    </Toolbar>
  )
}


export type TimeInputProps = {
  color?: 'primary' | 'secondary'
} & TimePickerProps

export const TimeInput = ({
  color = 'primary',
  ...props
}: TimeInputProps): JSX.Element => {
  const ToolbarComponent = (props: DatePickerToolbarProps): JSX.Element => (
    <ThemedTimeToolbarComponent
      color={color}
      date={props.date}
      openView={props.openView}
      setOpenView={props.setOpenView} />
  )
  return (
    <TimePicker
      ToolbarComponent={ToolbarComponent}
      {...props} />
  )
}
